import React, { useEffect, useState } from "react";
import { Alert, Button, Input, Spinner } from "reactstrap";
import {
  listCollectorMasterTerms,
  mergeCollectorMasterTerm,
  patchCollectorMasterTerm,
} from "../../services/collectorApi";

const FIELDS: { key: string; label: string }[] = [
  { key: "color", label: "Color" },
  { key: "itemCategory", label: "Category" },
  { key: "craftType", label: "Craft type" },
  { key: "fabric", label: "Fabric" },
  { key: "clusterType", label: "Region / cluster" },
  { key: "occasion", label: "Occasion" },
];

type Term = {
  id?: string;
  value?: string;
  label?: string;
  status?: string;
  usageCount?: number;
};

const CollectorMasterCuration: React.FC = () => {
  const [fieldKey, setFieldKey] = useState("color");
  const [terms, setTerms] = useState<Term[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [renameId, setRenameId] = useState("");
  const [renameLabel, setRenameLabel] = useState("");
  const [mergeFromId, setMergeFromId] = useState("");
  const [mergeTargetId, setMergeTargetId] = useState("");

  const load = (key = fieldKey) => {
    setLoading(true);
    setError("");
    listCollectorMasterTerms(key)
      .then((res) => setTerms(res.data?.data?.terms || []))
      .catch((e) => setError(e?.response?.data?.message || "Could not load contributed values"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load(fieldKey);
    setRenameId("");
    setMergeFromId("");
  }, [fieldKey]);

  const run = async (id: string, work: () => Promise<unknown>, ok: string) => {
    setBusyId(id);
    setError("");
    setMessage("");
    try {
      await work();
      setMessage(ok);
      await load();
      return true;
    } catch (e: any) {
      setError(e?.response?.data?.message || "Could not update that value");
      return false;
    } finally {
      setBusyId("");
    }
  };

  return (
    <section className="mc-card mb-3">
      <h2 className="h6 text-uppercase text-muted mb-2">Journal list corrections</h2>
      <p className="small text-muted">
        Other values typed by collectors are saved here once, then appear in everyone&apos;s dropdown.
        Fix spelling with Rename, combine duplicates with Merge, or Hide junk.
      </p>
      {message && <Alert color="success">{message}</Alert>}
      {error && <Alert color="danger">{error}</Alert>}
      <div className="d-flex flex-wrap gap-2 mb-3">
        {FIELDS.map((field) => (
          <Button
            key={field.key}
            type="button"
            className={fieldKey === field.key ? "mc-btn-primary" : "mc-btn-ghost"}
            onClick={() => setFieldKey(field.key)}
          >
            {field.label}
          </Button>
        ))}
      </div>
      {loading ? (
        <Spinner size="sm" />
      ) : terms.length === 0 ? (
        <p className="small text-muted mb-0">No contributed values yet for this list.</p>
      ) : (
        <ul className="list-unstyled mb-0">
          {terms.map((term) => {
            const id = term.id || "";
            return (
              <li key={id} className="border-bottom py-2">
                <div className="d-flex justify-content-between align-items-start gap-2 flex-wrap">
                  <div>
                    <div className="fw-semibold">{term.label || term.value}</div>
                    <small className="text-muted">
                      {term.status}
                      {term.usageCount != null ? ` · used ${term.usageCount}` : ""}
                    </small>
                  </div>
                  <div className="d-flex flex-wrap gap-2">
                    {term.status === "HIDDEN" ? (
                      <Button
                        type="button"
                        className="mc-btn-secondary"
                        disabled={busyId === id}
                        onClick={() => run(id, () => patchCollectorMasterTerm(id, { action: "RESTORE" }), "Restored to dropdowns")}
                      >
                        Restore
                      </Button>
                    ) : (
                      <Button
                        type="button"
                        className="mc-btn-ghost"
                        disabled={busyId === id}
                        onClick={() => run(id, () => patchCollectorMasterTerm(id, { action: "HIDE" }), "Hidden from dropdowns")}
                      >
                        Hide
                      </Button>
                    )}
                    <Button
                      type="button"
                      className="mc-btn-ghost"
                      disabled={busyId === id}
                      onClick={() => {
                        setRenameId(id);
                        setRenameLabel(term.label || term.value || "");
                      }}
                    >
                      Rename
                    </Button>
                    <Button
                      type="button"
                      className="mc-btn-ghost"
                      disabled={busyId === id}
                      onClick={() => {
                        setMergeFromId(id);
                        setMergeTargetId(terms.find((other) => other.id !== id)?.id || "");
                      }}
                    >
                      Merge
                    </Button>
                  </div>
                </div>
                {renameId === id && (
                  <div className="d-flex gap-2 mt-2">
                    <Input value={renameLabel} onChange={(e) => setRenameLabel(e.target.value)} />
                    <Button
                      type="button"
                      className="mc-btn-primary"
                      disabled={!renameLabel.trim() || busyId === id}
                      onClick={async () => {
                        const ok = await run(id, () => patchCollectorMasterTerm(id, { action: "RENAME", label: renameLabel }), "Spelling updated on this list and existing pieces");
                        if (ok) setRenameId("");
                      }}
                    >
                      Save
                    </Button>
                    <Button type="button" className="mc-btn-ghost" onClick={() => setRenameId("")}>
                      Cancel
                    </Button>
                  </div>
                )}
                {mergeFromId === id && (
                  <div className="d-flex gap-2 mt-2 align-items-center flex-wrap">
                    <span className="small">Merge into</span>
                    <Input
                      type="select"
                      value={mergeTargetId}
                      onChange={(e) => setMergeTargetId(e.target.value)}
                      style={{ maxWidth: 240 }}
                    >
                      {terms.filter((other) => other.id !== id).map((other) => (
                        <option key={other.id} value={other.id}>
                          {other.label || other.value}
                        </option>
                      ))}
                    </Input>
                    <Button
                      type="button"
                      className="mc-btn-primary"
                      disabled={!mergeTargetId || busyId === id}
                      onClick={async () => {
                        const ok = await run(id, () => mergeCollectorMasterTerm(id, mergeTargetId), "Merged. Existing pieces now use the kept spelling");
                        if (ok) setMergeFromId("");
                      }}
                    >
                      Merge
                    </Button>
                    <Button type="button" className="mc-btn-ghost" onClick={() => setMergeFromId("")}>
                      Cancel
                    </Button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
};

export default CollectorMasterCuration;
